/**
 * Face Guidance Logic
 * Calculates face alignment, position, distance, and tilt based on MediaPipe landmarks
 */

/**
 * Calculate face guidance status from MediaPipe landmarks
 * @param {Object} landmarks - MediaPipe face landmarks object
 * @param {number} videoWidth - Video element width
 * @param {number} videoHeight - Video element height
 * @param {Object} ovalBounds - {centerX, centerY, width, height} of the oval overlay
 * @param {boolean} [lightingOk=true] - Whether lighting is good; when false, guide shows lighting message and frame uses lighting color
 * @param {string} [lightingMessage=''] - Optional backend guidance when lighting not good (e.g. "Move to a brighter area")
 * @returns {Object} {isInsideFrame, statusColor, message}
 */
export const calculateFaceGuidance = (landmarks, videoWidth, videoHeight, ovalBounds, lightingOk = true, lightingMessage = '') => {
  const defaultLightingMessage = 'Improve lighting – move to a brighter area';
  const lightingMsg = (lightingMessage && String(lightingMessage).trim()) || defaultLightingMessage;
  if (!landmarks || !landmarks.length) {
    return {
      isInsideFrame: false,
      statusColor: lightingOk ? 'red' : 'orange',
      message: lightingOk ? 'Position your face' : lightingMsg,
    };
  }

  // MediaPipe FaceMesh landmark indices
  // Key landmarks: nose tip (1), left eye (33), right eye (263), forehead (10), chin (152)
  // Left cheek (234), right cheek (454)
  const NOSE_TIP = 1;
  const LEFT_EYE = 33;
  const RIGHT_EYE = 263;
  const FOREHEAD = 10;
  const CHIN = 152;
  const LEFT_CHEEK = 234;
  const RIGHT_CHEEK = 454;

  // Get normalized coordinates (0-1 range)
  // MediaPipe landmarks are an array of objects with x, y, z properties (normalized 0-1)
  const getLandmark = (index) => {
    if (!landmarks || !Array.isArray(landmarks) || !landmarks[index]) return null;
    const landmark = landmarks[index];
    // Handle both object format {x, y, z} and array format [x, y, z]
    const x = typeof landmark.x === 'number' ? landmark.x : (Array.isArray(landmark) ? landmark[0] : null);
    const y = typeof landmark.y === 'number' ? landmark.y : (Array.isArray(landmark) ? landmark[1] : null);
    
    if (x === null || y === null || typeof x !== 'number' || typeof y !== 'number') {
      return null;
    }
    
    return {
      x: x * videoWidth,
      y: y * videoHeight,
    };
  };

  const nose = getLandmark(NOSE_TIP);
  const leftEye = getLandmark(LEFT_EYE);
  const rightEye = getLandmark(RIGHT_EYE);
  const forehead = getLandmark(FOREHEAD);
  const chin = getLandmark(CHIN);
  const leftCheek = getLandmark(LEFT_CHEEK);
  const rightCheek = getLandmark(RIGHT_CHEEK);

  if (!nose || !leftEye || !rightEye) {
    return {
      isInsideFrame: false,
      statusColor: lightingOk ? 'red' : 'orange',
      message: lightingOk ? 'Position your face' : lightingMsg,
    };
  }

  const messages = [];
  let alignmentScore = 0;
  const maxScore = 5;

  // 1. Face Position (Centering) - Check if nose is within center ~50% of screen
  const centerX = videoWidth / 2;
  const noseOffsetX = Math.abs(nose.x - centerX);
  const centerThreshold = videoWidth * 0.25; // was 0.2 — easier left/right tolerance

  if (noseOffsetX > centerThreshold) {
    if (nose.x < centerX) {
      messages.push('Move face left');
    } else {
      messages.push('Move face right');
    }
  } else {
    alignmentScore++;
  }

  // 2. Face Distance (Move closer / back) - Compute distance between eyes
  // Adjust thresholds based on device type and FOV
  const deviceType = getDeviceType();
  const eyeDistance = Math.abs(rightEye.x - leftEye.x);
  const eyeDistanceNormalized = eyeDistance / videoWidth;

  // Closer-face window that is still easy to hit (between original lax and prior strict)
  let minDistanceThreshold, maxDistanceThreshold;
  switch (deviceType) {
    case 'mobile':
      minDistanceThreshold = 0.14;
      maxDistanceThreshold = 0.42;
      break;
    case 'tablet':
      minDistanceThreshold = 0.15;
      maxDistanceThreshold = 0.43;
      break;
    case 'small-laptop':
      minDistanceThreshold = 0.15;
      maxDistanceThreshold = 0.44;
      break;
    case 'desktop':
    default:
      minDistanceThreshold = 0.16;
      maxDistanceThreshold = 0.45;
      break;
  }

  if (eyeDistanceNormalized < minDistanceThreshold) {
    messages.push('Move closer');
  } else if (eyeDistanceNormalized > maxDistanceThreshold) {
    messages.push('Move a little farther');
  } else {
    alignmentScore++;
  }

  // 3. Head Tilt (Left / Right) - Calculate angle between eyes
  const eyeAngle = Math.atan2(rightEye.y - leftEye.y, rightEye.x - leftEye.x) * (180 / Math.PI);
  const tiltThreshold = 16;

  if (eyeAngle > tiltThreshold) {
    messages.push('Tilt head right');
  } else if (eyeAngle < -tiltThreshold) {
    messages.push('Tilt head left');
  } else {
    alignmentScore++;
  }

  // 4. Face Height (Chin up/down) - Compare nose Y with forehead and chin
  if (forehead && chin) {
    const faceHeight = Math.abs(chin.y - forehead.y);
    const noseRelativeY = (nose.y - forehead.y) / faceHeight;

    if (noseRelativeY < 0.28) {
      messages.push('Lower your head');
    } else if (noseRelativeY > 0.62) {
      messages.push('Raise your head');
    } else {
      alignmentScore++;
    }
  }

  // 5. Fit Inside Rounded Rectangle Guide (relaxed: 3 of 4 key points)
  let allInsideGuide = true;
  let guidePointsInside = 0;
  if (ovalBounds && forehead && chin && leftCheek && rightCheek) {
    const checkInsideRectangle = (point) => {
      const padX = ovalBounds.width * 0.08;
      const padY = ovalBounds.height * 0.08;
      const left = ovalBounds.centerX - ovalBounds.width / 2 - padX;
      const right = ovalBounds.centerX + ovalBounds.width / 2 + padX;
      const top = ovalBounds.centerY - ovalBounds.height / 2 - padY;
      const bottom = ovalBounds.centerY + ovalBounds.height / 2 + padY;
      return point.x >= left && point.x <= right &&
             point.y >= top && point.y <= bottom;
    };

    const points = [forehead, chin, leftCheek, rightCheek];
    guidePointsInside = points.filter(checkInsideRectangle).length;
    allInsideGuide = guidePointsInside >= 3;
  }

  if (allInsideGuide) {
    alignmentScore++;
  }

  // Determine status color and message
  // When lighting is not good, show lighting guide and use orange frame color
  if (!lightingOk) {
    const isFaceNear = eyeDistanceNormalized >= minDistanceThreshold && eyeDistanceNormalized <= maxDistanceThreshold;
    const isFaceStraight = Math.abs(eyeAngle) <= 16;
    return {
      isInsideFrame: false,
      statusColor: 'orange',
      message: lightingMsg,
      alignmentScore,
      maxScore,
      isFaceNear,
      isFaceStraight,
      eyeDistanceNormalized,
      eyeAngle,
    };
  }

  // Easier green: score >= 3, closer face ok, allow one soft non-distance issue
  let statusColor = 'red';
  let message = 'Position your face';

  let prioritizedMessage = null;
  const distanceMessage = messages.find(msg => msg === 'Move closer' || msg === 'Move a little farther');
  if (distanceMessage) {
    prioritizedMessage = distanceMessage;
  }

  const distanceOk = !distanceMessage;
  if (alignmentScore >= 3 && allInsideGuide && distanceOk) {
    statusColor = 'green';
    message = 'Perfect alignment!';
  } else if (alignmentScore >= 2 && messages.length <= 1) {
    statusColor = 'yellow';
    message = prioritizedMessage || messages[0] || 'Hold still';
  } else {
    statusColor = 'red';
    if (prioritizedMessage) {
      message = prioritizedMessage;
    } else {
      const positionMsg = messages.find(msg => msg.includes('Move face'));
      const tiltMsg = messages.find(msg => msg.includes('Tilt head'));
      const heightMsg = messages.find(msg => msg.includes('head'));
      message = positionMsg || tiltMsg || heightMsg || messages[0] || 'Adjust your position';
    }
  }

  const isFaceNear = eyeDistanceNormalized >= minDistanceThreshold && eyeDistanceNormalized <= maxDistanceThreshold;

  // Match tilt guidance so capture is not stricter than the HUD
  const STRAIGHT_FACE_THRESHOLD = 16;
  const isFaceStraight = Math.abs(eyeAngle) <= STRAIGHT_FACE_THRESHOLD;

  return {
    isInsideFrame: allInsideGuide && alignmentScore >= 3,
    statusColor,
    message,
    alignmentScore,
    maxScore,
    isFaceNear,
    isFaceStraight,
    eyeDistanceNormalized,
    eyeAngle,
  };
};

/**
 * Check if a point is inside a rounded rectangle
 * @param {Object} point - {x, y}
 * @param {Object} rect - {centerX, centerY, width, height}
 * @returns {boolean}
 */
export const isPointInsideRectangle = (point, rect) => {
  const left = rect.centerX - rect.width / 2;
  const right = rect.centerX + rect.width / 2;
  const top = rect.centerY - rect.height / 2;
  const bottom = rect.centerY + rect.height / 2;
  
  return point.x >= left && point.x <= right && 
         point.y >= top && point.y <= bottom;
};

/**
 * Detect device type based on screen width
 * @returns {string} Device type: 'mobile', 'tablet', 'small-laptop', or 'desktop'
 */
const getDeviceType = () => {
  if (typeof window === 'undefined') return 'desktop';
  const width = window.innerWidth;
  if (width < 768) return 'mobile'; // Mobile devices
  if (width < 1024) return 'tablet'; // Tablets
  if (width < 1440) return 'small-laptop'; // Small laptops
  return 'desktop'; // Desktop and large screens
};

/**
 * Calculate guide shape bounds (square) - responsive based on device type
 * Creates a square guide centered in the frame with different sizes for different devices
 * @param {number} width - Video/image width
 * @param {number} height - Video/image height
 * @returns {Object} {centerX, centerY, width, height}
 */
export const calculateGuideBounds = (width, height) => {
  const deviceType = getDeviceType();
  const minDimension = Math.min(width, height);
  
  // Different guide sizes based on device type and FOV
  let squareSizeMultiplier;
  switch (deviceType) {
    case 'mobile':
      // Mobile: Larger guide box (70%) - selfie camera has wider FOV
      squareSizeMultiplier = 0.70;
      break;
    case 'tablet':
      // Tablet: Medium-large guide box (68%)
      squareSizeMultiplier = 0.68;
      break;
    case 'small-laptop':
      // Small laptop: Medium guide box (66%)
      squareSizeMultiplier = 0.66;
      break;
    case 'desktop':
    default:
      // Desktop: Standard guide box (65%) - webcam has narrower FOV
      squareSizeMultiplier = 0.65;
      break;
  }
  
  const squareSize = minDimension * squareSizeMultiplier;
  const centerX = width / 2;
  const centerY = height * (deviceType === 'mobile' || deviceType === 'tablet' ? 0.54 : 0.5);
  
  return {
    centerX,
    centerY,
    width: squareSize,
    height: squareSize, // Same as width to make it a perfect square
  };
};

/**
 * Calculate side profile face guidance (left/right).
 * Same structure as calculateFaceGuidance for unified overlay.
 * @param {Object} landmarks - MediaPipe face landmarks
 * @param {number} videoWidth - Video width
 * @param {number} videoHeight - Video height
 * @param {string} targetPose - 'left' | 'right' (UI: left step → prompt “turn right”; right step → prompt “turn left” — natural selfie wording)
 * @param {boolean} [lightingOk=true]
 * @param {string} [lightingMessage='']
 */
export const calculateSideFaceGuidance = (landmarks, videoWidth, videoHeight, targetPose, lightingOk = true, lightingMessage = '') => {
  const defaultLightingMessage = 'Improve lighting – move to a brighter area';
  const lightingMsg = (lightingMessage && String(lightingMessage).trim()) || defaultLightingMessage;
  if (!landmarks || landmarks.length < 264) {
    return {
      isInsideFrame: false,
      statusColor: lightingOk ? 'red' : 'orange',
      message: !lightingOk ? lightingMsg : (targetPose === 'left' ? 'Turn your head to the right' : 'Turn your head to the left'),
      isFaceNear: false,
      isFaceStraight: false,
    };
  }

  const NOSE_TIP = 1;
  const LEFT_EYE = 33;
  const RIGHT_EYE = 263;
  const FRONT_OFFSET = 0.12;
  const SIDE_OFFSET = 0.14;
  const MAX_HEAD_TILT_DEG = 18;

  const getCoord = (lm, axis) => (typeof lm[axis] === 'number' ? lm[axis] : (axis === 'x' ? lm.x : lm.y));

  let minX = 1, maxX = 0, minY = 1, maxY = 0;
  landmarks.forEach((lm) => {
    const x = getCoord(lm, 'x');
    const y = getCoord(lm, 'y');
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y);
  });

  const faceWidth = maxX - minX;
  const faceCenterX = (minX + maxX) / 2;
  const nose = landmarks[NOSE_TIP];
  const leftEye = landmarks[LEFT_EYE];
  const rightEye = landmarks[RIGHT_EYE];

  const noseX = getCoord(nose, 'x');
  const noseY = getCoord(nose, 'y');
  const leftEyeY = getCoord(leftEye, 'y');
  const rightEyeY = getCoord(rightEye, 'y');
  const leftEyeX = getCoord(leftEye, 'x');
  const rightEyeX = getCoord(rightEye, 'x');

  const noseOffsetFromCenter = noseX - faceCenterX;
  const offsetRatio = faceWidth > 0 ? noseOffsetFromCenter / faceWidth : 0;

  const rollDeg = Math.atan2(rightEyeY - leftEyeY, rightEyeX - leftEyeX) * (180 / Math.PI);
  const eyeMidY = (leftEyeY + rightEyeY) / 2;
  const noseOffsetY = Math.abs(noseY - eyeMidY);

  if (Math.abs(rollDeg) > MAX_HEAD_TILT_DEG) {
    return {
      isInsideFrame: false,
      statusColor: 'red',
      message: 'Keep head level',
      isFaceNear: true,
      isFaceStraight: false,
    };
  }
  if (noseOffsetY > 0.2) {
    return {
      isInsideFrame: false,
      statusColor: 'red',
      message: 'Keep head level',
      isFaceNear: true,
      isFaceStraight: false,
    };
  }

  // Nose offset vs face box: negative = nose left (left cheek toward camera / "left profile");
  // positive = nose right (right cheek toward camera / "right profile"). Matches front-facing webcam.
  const isLeftProfile = offsetRatio < -SIDE_OFFSET;
  const isRightProfile = offsetRatio > SIDE_OFFSET;
  const isFront = Math.abs(offsetRatio) <= FRONT_OFFSET;

  if (targetPose === 'left') {
    if (isLeftProfile) {
      return {
        isInsideFrame: true,
        statusColor: 'green',
        message: 'Perfect! Hold still.',
        isFaceNear: true,
        isFaceStraight: true,
      };
    }
    if (isFront) {
      return {
        isInsideFrame: false,
        statusColor: 'yellow',
        message: 'Turn your head to the right',
        isFaceNear: true,
        isFaceStraight: true,
      };
    }
    return {
      isInsideFrame: false,
      statusColor: 'red',
      message: 'Turn your head to the right',
      isFaceNear: true,
      isFaceStraight: true,
    };
  }

  if (targetPose === 'right') {
    if (isRightProfile) {
      return {
        isInsideFrame: true,
        statusColor: 'green',
        message: 'Perfect! Hold still.',
        isFaceNear: true,
        isFaceStraight: true,
      };
    }
    if (isFront) {
      return {
        isInsideFrame: false,
        statusColor: 'yellow',
        message: 'Turn your head to the left',
        isFaceNear: true,
        isFaceStraight: true,
      };
    }
    return {
      isInsideFrame: false,
      statusColor: 'red',
      message: 'Turn your head to the left',
      isFaceNear: true,
      isFaceStraight: true,
    };
  }

  return {
    isInsideFrame: false,
    statusColor: 'red',
    message: 'Position your face',
    isFaceNear: true,
    isFaceStraight: false,
  };
};

/**
 * Calculate head rotation angle from landmarks
 * @param {Object} landmarks - MediaPipe landmarks
 * @param {number} videoWidth - Video width
 * @param {number} videoHeight - Video height
 * @returns {number} Angle in degrees
 */
export const calculateHeadTilt = (landmarks, videoWidth, videoHeight) => {
  if (!landmarks || landmarks.length < 2) return 0;

  const LEFT_EYE = 33;
  const RIGHT_EYE = 263;

  const leftEye = landmarks[LEFT_EYE];
  const rightEye = landmarks[RIGHT_EYE];

  if (!leftEye || !rightEye) return 0;

  const leftEyeX = leftEye.x * videoWidth;
  const leftEyeY = leftEye.y * videoHeight;
  const rightEyeX = rightEye.x * videoWidth;
  const rightEyeY = rightEye.y * videoHeight;

  const angle = Math.atan2(rightEyeY - leftEyeY, rightEyeX - leftEyeX) * (180 / Math.PI);
  return angle;
};

